import { apiRequest } from "./api";
import { UMBRAL_POR_DEFECTO, type UmbralBotica } from "@huayruro/shared";
import type { AccionLocal, HuayruroDB, PreguntaLocal, RecomendacionLocal, ReglaLocal } from "./db-local";

// Sync de catálogo (pull, §9): trae el delta desde el último sync y lo aplica a Dexie, con
// tombstones. Lecturas del POS = Dexie (funciona offline). Primer arranque = sync completo.

type SyncDelta = {
  server_time: string;
  productos: {
    id: string; nombre: string; presentacion: string | null; laboratorio: string | null;
    principio_activo: string | null; categoria: string | null; tipo?: string | null; requiere_receta?: number; activo: number; deleted_at: string | null; updated_at: string;
  }[];
  presentaciones: { id: string; producto_id: string; nombre: string; factor_unidades: number; es_base: number }[];
  codigos: { gtin: string; producto_id: string; presentacion_id: string | null }[];
  precios: { producto_id: string; presentacion_id: string; precio_sin_igv_dm: number; precio_total_dm: number; vigente_desde: string }[];
  // Hora del último «deshacer esta carga» del tenant (null si nunca hubo). Lo borrado no llega como cambio.
  reinicio?: string | null;
};

const META_ULTIMO_SYNC = "ultimo_sync_catalogo";
export const META_DUENO_COPIA = "dueno_copia";
// Qué campos trae la copia. La 2 suma `requiere_receta` (freno de receta, 26-oct-2026): una copia
// anterior no lo tiene y un delta solo trae lo que cambió, así que se pide entera una vez.
const META_FORMATO_COPIA = "formato_copia";
const FORMATO_COPIA = 2;

// Tira la copia del catálogo (productos, presentaciones, códigos y precios) y la marca de la última
// copia, para que la siguiente sea completa. La cola de ventas pendientes NO se toca nunca.
async function tirarCopiaCatalogo(db: HuayruroDB, conStock: boolean): Promise<void> {
  const tablas = [db.productos, db.presentaciones, db.codigos, db.precios, db.meta, ...(conStock ? [db.stock_cache] : [])];
  await db.transaction("rw", tablas, async () => {
    await Promise.all([db.productos.clear(), db.presentaciones.clear(), db.codigos.clear(), db.precios.clear()]);
    if (conStock) await db.stock_cache.clear();
    await db.meta.delete(META_ULTIMO_SYNC);
  });
}

// Tarea 2.6 (plan del arranque de octubre): los precios y el stock de la copia son de UNA botica y se
// guardan sin decir de cuál. Si en este equipo entra otra cuenta u otra botica, la copia vieja se tira
// antes de pedir la nueva; si no, se verían precios de la botica anterior. Devuelve si tiró la copia.
export async function asegurarDuenoCopia(db: HuayruroDB, dueno: string): Promise<boolean> {
  const actual = (await db.meta.get(META_DUENO_COPIA))?.valor as string | undefined;
  if (actual === dueno) return false;
  const habiaCopia = actual !== undefined || (await db.productos.count()) > 0;
  if (habiaCopia) await tirarCopiaCatalogo(db, true);
  await db.meta.put({ clave: META_DUENO_COPIA, valor: dueno });
  return habiaCopia;
}

// `botica`: solo la del dueño (la que eligió en el Mostrador); los demás roles la sacan de su cuenta.
// Se pasa explícita porque el pull también corre con el panel abierto, donde el header del Mostrador
// está apagado.
export async function sincronizarCatalogo(db: HuayruroDB, getToken: () => string | null, botica: string | null = null): Promise<{ aplicados: number }> {
  const formato = (await db.meta.get(META_FORMATO_COPIA))?.valor as number | undefined;
  const desde = formato === FORMATO_COPIA ? ((await db.meta.get(META_ULTIMO_SYNC))?.valor as string | undefined) ?? "" : "";
  let delta = await apiRequest<SyncDelta>(`/catalogo/sync?desde=${encodeURIComponent(desde)}`, { token: getToken(), botica });
  // Hubo un «deshacer esta carga» DESPUÉS de la última copia: lo que se borró no llega como cambio, así
  // que se tira la copia y se pide entera (las horas son las del servidor en los dos lados).
  if (desde && delta.reinicio && delta.reinicio > desde) {
    await tirarCopiaCatalogo(db, false);
    delta = await apiRequest<SyncDelta>(`/catalogo/sync?desde=`, { token: getToken(), botica });
  }

  const vivos = delta.productos.filter((p) => !p.deleted_at);
  const muertos = delta.productos.filter((p) => p.deleted_at).map((p) => p.id);

  await db.transaction("rw", [db.productos, db.presentaciones, db.codigos, db.precios, db.meta], async () => {
    if (vivos.length) {
      await db.productos.bulkPut(
        vivos.map((p) => ({
          id: p.id, nombre: p.nombre, presentacion: p.presentacion, laboratorio: p.laboratorio,
          principio_activo: p.principio_activo, categoria: p.categoria, tipo: p.tipo ?? null,
          requiere_receta: p.requiere_receta === 1 ? 1 : 0, activo: p.activo, updated_at: p.updated_at,
        })),
      );
    }
    if (delta.presentaciones.length) await db.presentaciones.bulkPut(delta.presentaciones);
    if (delta.codigos.length) await db.codigos.bulkPut(delta.codigos);
    if (delta.precios.length) {
      await db.precios.bulkPut(
        delta.precios.map((pr) => ({ id: `${pr.producto_id}:${pr.presentacion_id}`, ...pr })),
      );
    }
    // Tombstones: purgar productos borrados y sus dependientes.
    for (const id of muertos) {
      await db.productos.delete(id);
      await db.presentaciones.where("producto_id").equals(id).delete();
      await db.codigos.where("producto_id").equals(id).delete();
      await db.precios.where("producto_id").equals(id).delete();
    }
    await db.meta.put({ clave: META_ULTIMO_SYNC, valor: delta.server_time });
    await db.meta.put({ clave: META_FORMATO_COPIA, valor: FORMATO_COPIA });
  });

  return { aplicados: delta.productos.length + delta.presentaciones.length + delta.codigos.length + delta.precios.length };
}

// Sync de stock (informativo, §9 stock_cache): trae el inventario de MI sucursal y actualiza la
// cache. El stock se muestra con sello de hora; la verdad la impone el server al vender.
// `completo=1` (16-oct-2026): TODO el inventario de la botica, no los primeros 500 de la pantalla. Trae también
// la ubicación de cada producto y el umbral de «quedan pocas» de la botica.
type InventarioResp = {
  inventario: { producto_id: string; stock_unidades: number; stock_minimo: number; ubicacion?: string | null; updated_at: string }[];
  umbral?: UmbralBotica;
};
export const META_UMBRAL = "umbral_botica";

export async function sincronizarStock(db: HuayruroDB, getToken: () => string | null, botica: string | null = null): Promise<{ aplicados: number }> {
  const r = await apiRequest<InventarioResp>("/inventario?completo=1", { token: getToken(), botica });
  await db.transaction("rw", db.stock_cache, db.meta, async () => {
    if (r.inventario.length) {
      await db.stock_cache.bulkPut(
        r.inventario.map((i) => ({
          producto_id: i.producto_id,
          stock_unidades: i.stock_unidades,
          stock_minimo: i.stock_minimo,
          ubicacion: i.ubicacion ?? null,
          updated_at: i.updated_at,
        })),
      );
    }
    await db.meta.put({ clave: META_UMBRAL, valor: r.umbral ?? UMBRAL_POR_DEFECTO });
  });
  return { aplicados: r.inventario.length };
}

// Sync de reglas de venta cruzada (A4). Son de tenant y son poquísimas: se reemplaza la tabla
// entera en cada pull, así una regla apagada o borrada por el admin desaparece del mostrador sin
// necesidad de tombstones. Si falla (sin red), el motor sigue con las reglas que ya tenía.
type ReglasResp = { reglas: ReglaLocal[] };

export async function sincronizarReglas(db: HuayruroDB, getToken: () => string | null): Promise<{ reglas: number }> {
  const r = await apiRequest<ReglasResp>("/sugerencias/reglas", { token: getToken(), botica: null });
  await db.transaction("rw", db.reglas, async () => {
    await db.reglas.clear();
    if (r.reglas.length) await db.reglas.bulkPut(r.reglas);
  });
  return { reglas: r.reglas.length };
}

// Copiloto (etapa 1, 16-oct-2026): mismo trato que las reglas — pocas filas de la cadena, se reemplazan
// enteras; sin red, el panel sigue con lo que ya tenía.
type CopilotoResp = { preguntas: PreguntaLocal[]; recomendaciones: RecomendacionLocal[]; acciones?: AccionLocal[] };

export async function sincronizarCopiloto(db: HuayruroDB, getToken: () => string | null): Promise<{ preguntas: number; recomendaciones: number }> {
  const r = await apiRequest<CopilotoResp>("/copiloto", { token: getToken(), botica: null });
  await db.transaction("rw", [db.copiloto_preguntas, db.copiloto_recomendaciones, db.copiloto_acciones], async () => {
    await db.copiloto_preguntas.clear();
    await db.copiloto_recomendaciones.clear();
    await db.copiloto_acciones.clear();
    if (r.preguntas.length) await db.copiloto_preguntas.bulkPut(r.preguntas);
    if (r.recomendaciones.length) await db.copiloto_recomendaciones.bulkPut(r.recomendaciones);
    if (r.acciones?.length) await db.copiloto_acciones.bulkPut(r.acciones);
  });
  return { preguntas: r.preguntas.length, recomendaciones: r.recomendaciones.length };
}

// Normaliza para búsqueda local sin tildes (equivalente en cliente al remove_diacritics de FTS5).
export const sinTildes = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
